import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api, { rupees, formatApiError, BRAND_COLORS } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, QrCode, Send, Gift, RotateCw } from "lucide-react";

// --- Scratch card: plays on first tap (guarded) ---
function ScratchCard({ onPlay, onDone }) {
  const [prize, setPrize] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const busy = useRef(false);
  const handle = async () => {
    if (revealed || busy.current) return;
    busy.current = true;
    const p = await onPlay();
    if (!p) { busy.current = false; return; }
    setPrize(p); setRevealed(true);
    setTimeout(onDone, 1000);
  };
  return (
    <div className="relative w-56 h-28 mx-auto rounded-xl overflow-hidden select-none cursor-pointer" data-testid="consumer-simulator-play-scratch-card" onClick={handle}>
      <div className="absolute inset-0 flex flex-col items-center justify-center bg-white border-2 border-dashed border-violet-300 rounded-xl">
        <Gift className="text-violet-500 mb-1" size={22} />
        <span className="text-lg font-bold text-violet-700 font-head">{prize ? (prize.prize_points > 0 ? `+${prize.prize_points} pts` : "No win") : "🎁"}</span>
        <span className="text-[11px] text-slate-400">{prize?.prize_label || "Scratch to reveal"}</span>
      </div>
      <div className={`scratch-cover absolute inset-0 tnp-gradient flex items-center justify-center text-white font-semibold text-sm ${revealed ? "opacity-0 pointer-events-none" : "opacity-100"}`}>Tap to scratch</div>
    </div>
  );
}

// --- Spin wheel: plays on spin click (guarded) ---
function SpinWheel({ onPlay, onDone }) {
  const [angle, setAngle] = useState(0);
  const [prize, setPrize] = useState(null);
  const [spun, setSpun] = useState(false);
  const busy = useRef(false);
  const colors = ["#6D28D9", "#DB2777", "#D97706", "#15803D", "#0EA5E9", "#9A3412"];
  const spin = async () => {
    if (spun || busy.current) return;
    busy.current = true;
    const p = await onPlay();
    if (!p) { busy.current = false; return; }
    setPrize(p); setSpun(true);
    setAngle(360 * 5 + Math.floor(Math.random() * 360));
    setTimeout(onDone, 4300);
  };
  return (
    <div className="flex flex-col items-center">
      <div className="relative w-40 h-40">
        <div className="absolute -top-1 left-1/2 -translate-x-1/2 z-10 w-0 h-0 border-l-8 border-r-8 border-t-[14px] border-l-transparent border-r-transparent border-t-violet-700" />
        <div className="wheel-spin w-40 h-40 rounded-full border-4 border-white shadow-lg" style={{ transform: `rotate(${angle}deg)`, background: `conic-gradient(${colors.map((c, i) => `${c} ${i * 60}deg ${(i + 1) * 60}deg`).join(",")})` }} />
      </div>
      {!spun ? (
        <button data-testid="consumer-simulator-spin-wheel-btn" onClick={spin} className="mt-3 tnp-gradient text-white text-sm font-semibold px-5 py-2 rounded-full flex items-center gap-1.5"><RotateCw size={14} /> Spin</button>
      ) : (
        <div className="mt-3 text-sm font-bold text-violet-700">{prize?.prize_points > 0 ? `You won +${prize.prize_points} pts!` : "Better luck next time"}</div>
      )}
    </div>
  );
}

export default function Simulator() {
  const nav = useNavigate();
  const [scenario, setScenario] = useState(null);
  const [step, setStep] = useState("scan");
  const [msgs, setMsgs] = useState([]);
  const [customerId, setCustomerId] = useState(null);
  const [brandId, setBrandId] = useState("brownsugar");
  const [game, setGame] = useState(null);
  const gameResultRef = useRef(null);
  const [upi, setUpi] = useState("");
  const [redeemPts, setRedeemPts] = useState(100);
  const [balance, setBalance] = useState(0);
  const [chatInput, setChatInput] = useState("");
  const scrollRef = useRef(null);

  const fetchScenario = async () => {
    try { const { data } = await api.get("/sim/scenario"); setScenario(data); setBrandId(data.brand.id); return data; }
    catch (e) { toast.error("No active QR available. Create & activate a batch in the admin portal."); return null; }
  };
  useEffect(() => { fetchScenario(); }, []);
  useEffect(() => { scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight); }, [msgs, step]);

  const bot = (text) => setMsgs((m) => [...m, { from: "bot", text }]);
  const me = (text) => setMsgs((m) => [...m, { from: "me", text }]);

  const join = async () => {
    try {
      me("Scanned a product QR 📷");
      const r = await api.get(`/qr/resolve/${scenario.token}`);
      bot(`Welcome to ${r.data.brand.name} Rewards! 🎉 (Powered by TagnPay)\nProduct: ${r.data.sku_name}\n\nReply to accept program terms & claim your points.`);
      setStep("joined");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const claim = async () => {
    me("I accept the terms ✅");
    try {
      const phone = "+9199" + Math.floor(10000000 + Math.random() * 89999999);
      const { data } = await api.post("/qr/claim", { token: scenario.token, phone, name: "Simulator User", state: "Maharashtra" });
      setCustomerId(data.customer_id);
      const r = data.receipt;
      setBalance(r.balance);
      bot(`🧾 Receipt\n${r.sku_name}\nBase points: ${r.base_points}\nScheme bonus: ${r.bonus_points}\nTotal earned: ${r.total_points}\nBalance: ${r.balance} pts`);
      if (data.game_available) { setGame(data.game_available); setStep("game"); bot(`🎁 You've unlocked ${data.game_available.name}! Tap to play.`); }
      else setStep("redeem");
    } catch (e) {
      const status = e.response?.status;
      toast.error(formatApiError(e.response?.data?.detail));
      if (status === 409 || status === 400) { bot("That code was already used. Fetching a fresh demo product…"); const s = await fetchScenario(); if (s) setStep("scan"); }
    }
  };

  const playGame = async () => {
    try {
      const { data } = await api.post(`/games/${game.id}/play`, { customer_id: customerId });
      gameResultRef.current = data;
      return data;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); return null; }
  };

  const afterGame = async () => {
    const res = gameResultRef.current;
    if (res?.prize_points > 0) bot(`🎉 You won ${res.prize_points} points!`);
    else bot("No win this time — your base reward is safe!");
    try { const { data } = await api.get(`/customers/${customerId}`); setBalance(data.balances[brandId] || 0); } catch (e) {}
    setStep("redeem");
  };

  const redeem = async () => {
    if (!upi.includes("@")) { toast.error("Enter a valid UPI ID"); return; }
    me(`Redeem ${redeemPts} pts to ${upi}`);
    try {
      const { data } = await api.post("/redemptions", { customer_id: customerId, brand_id: brandId, points: parseInt(redeemPts), upi });
      bot(`💸 Payout submitted: ${rupees(data.amount_paise)}\nStatus: ${data.status}\nYou'll get the UTR once reconciled. (We never ask for your UPI PIN or bank OTP.)`);
      setStep("feedback");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const sendFeedback = async (rating) => {
    me(`Rated ${rating}★`);
    try {
      await api.post("/feedback/responses", { customer_id: customerId, brand_id: brandId, rating, comment: "", touchpoint: "after_redeem" });
      bot("Thank you for your feedback! 💜 Your rewards journey is complete.");
      setStep("done");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const sendChat = async () => {
    if (!chatInput.trim()) return;
    const q = chatInput; setChatInput(""); me(q);
    try { const { data } = await api.post("/sim/chat", { customer_id: customerId, message: q }); bot(data.reply); }
    catch (e) { bot("Assistant unavailable right now."); }
  };

  const accent = BRAND_COLORS[brandId] || "#6D28D9";

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col items-center py-6 px-4">
      <div className="w-full max-w-md flex items-center justify-between mb-4">
        <button onClick={() => nav(-1)} className="flex items-center gap-1.5 text-sm text-slate-600 font-semibold"><ArrowLeft size={16} /> Back</button>
        <div className="text-xs font-semibold text-violet-700 bg-violet-50 border border-violet-100 px-2.5 py-1 rounded-full">Consumer Simulator · Sandbox</div>
      </div>

      <div className="w-[380px] max-w-full bg-black rounded-[2.5rem] p-2.5 shadow-2xl">
        <div className="bg-white rounded-[2rem] overflow-hidden flex flex-col h-[680px]">
          <div className="px-4 py-3 flex items-center gap-3 text-white" style={{ background: "#075E54" }}>
            <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center font-bold">S</div>
            <div className="flex-1"><div className="font-semibold text-sm">SUGAR Group Rewards</div><div className="text-[11px] opacity-80">online · Powered by TagnPay</div></div>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto chat-bg p-3 space-y-2">
            {msgs.length === 0 && (
              <div className="text-center mt-10">
                <div className="w-20 h-20 mx-auto rounded-2xl flex items-center justify-center mb-3" style={{ background: accent + "22", color: accent }}><QrCode size={34} /></div>
                <p className="text-sm text-slate-500 px-6">{scenario ? `Scan a ${scenario.brand.name} product to begin your rewards journey.` : "Loading sandbox scenario…"}</p>
              </div>
            )}
            {msgs.map((m, i) => (
              <div key={i} className={`flex ${m.from === "me" ? "justify-end" : "justify-start"} fade-in`}>
                <div className={`max-w-[78%] px-3 py-2 rounded-lg text-sm whitespace-pre-line ${m.from === "me" ? "bg-[#DCF8C6] text-slate-800" : "bg-white text-slate-700 shadow-sm"}`}>{m.text}</div>
              </div>
            ))}
            {step === "game" && game && (
              <div className="bg-white rounded-xl p-3 shadow-sm fade-in">
                {game.game_type === "spin"
                  ? <SpinWheel onPlay={playGame} onDone={afterGame} />
                  : <ScratchCard onPlay={playGame} onDone={afterGame} />}
              </div>
            )}
          </div>

          <div className="border-t border-slate-200 p-3 bg-white">
            {step === "scan" && <button data-testid="consumer-simulator-scan-qr-btn" disabled={!scenario} onClick={join} className="w-full tnp-gradient text-white font-semibold py-2.5 rounded-full flex items-center justify-center gap-2 disabled:opacity-50"><QrCode size={16} /> Scan Product QR</button>}
            {step === "joined" && <button data-testid="consumer-simulator-join-whatsapp-btn" onClick={claim} className="w-full tnp-gradient text-white font-semibold py-2.5 rounded-full">Accept & Claim Points</button>}
            {step === "redeem" && (
              <div className="space-y-2">
                <div className="text-xs text-slate-500 text-center">Balance: {balance} pts · min 100 to redeem</div>
                <input data-testid="consumer-simulator-upi-id-input" value={upi} onChange={(e) => setUpi(e.target.value)} placeholder="yourname@upi" className="w-full px-3 py-2 rounded-full border border-slate-200 text-sm" />
                <div className="flex gap-2">
                  <input type="number" value={redeemPts} onChange={(e) => setRedeemPts(e.target.value)} className="w-24 px-3 py-2 rounded-full border border-slate-200 text-sm" />
                  <button data-testid="consumer-simulator-redeem-upi-btn" onClick={redeem} className="flex-1 tnp-gradient text-white font-semibold py-2 rounded-full">Redeem to UPI</button>
                </div>
              </div>
            )}
            {step === "feedback" && (
              <div>
                <div className="text-xs text-slate-500 text-center mb-2">Rate your experience</div>
                <div className="flex justify-center gap-2">{[1, 2, 3, 4, 5].map((r) => <button key={r} data-testid={r === 5 ? "consumer-simulator-submit-feedback-btn" : `feedback-star-${r}`} onClick={() => sendFeedback(r)} className="text-2xl hover:scale-125 transition-transform">⭐</button>)}</div>
              </div>
            )}
            {(step === "done" || step === "redeem" || step === "feedback") && (
              <div className="flex gap-2 mt-2">
                <input value={chatInput} onChange={(e) => setChatInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendChat()} placeholder="Ask the assistant…" className="flex-1 px-3 py-2 rounded-full border border-slate-200 text-sm" data-testid="consumer-chat-input" />
                <button onClick={sendChat} className="w-10 h-10 rounded-full tnp-gradient text-white flex items-center justify-center"><Send size={15} /></button>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="mt-4 text-xs text-slate-400">SUGAR Cosmetics Group Rewards · Powered by TagnPay</div>
    </div>
  );
}
